<script setup lang="ts">
//#region import
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash-es';
import { useApi } from '@/api';
import type { EmitsToListeners } from '@/types/core';
//#endregion

interface  Emits {
  'close': [],
  'success': []
};

export interface EditPasswordParams {
  listeners?: EmitsToListeners<Emits>
}

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const { t } = useI18n();
//#endregion

//#region data
const isVisible = ref(false);
const errors = ref<any>({});
const resetPassword = reactive({
  old_password: '',
  new_password: '',
});
const repeat_new_password = ref('');
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};
const handleSave = () => {
  errors.value = {};
  if (resetPassword.new_password !== repeat_new_password.value) {
    errors.value.new_password = t('profile_popup.repeat_error');
    return;
  }
  api.postUpdatePassword(resetPassword).then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    errors.value = cloneDeep(e.data.errors);
  });
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="edit-password-popup"
    :title="$t('profile_popup.reset_password_title')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="resetPassword.old_password"
          :placeholder="$t('profile_popup.reset_password_old_placeholder')"
          :label="$t('profile_popup.reset_password_old_label')"
          :error="errors.old_password"
          autosize
          type="password"
        />
        <UiInput
          v-model="resetPassword.new_password"
          :placeholder="$t('profile_popup.reset_password_new_placeholder')"
          :label="$t('profile_popup.reset_password_new_label')"
          :error="errors.new_password"
          autosize
          type="password"
        />
        <UiInput
          v-model="repeat_new_password"
          :placeholder="$t('profile_popup.reset_password_repeat_placeholder')"
          :label="$t('profile_popup.reset_password_repeat_label')"
          :error="errors.new_password"
          autosize
          type="password"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('profile_popup.reset_password_action') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.edit-password-popup {

}
</style>
